/**
 * Global-search domain — pure, React-free result mapping, filtering and grouping.
 *
 * The search aggregates several sources (patient/invoice records searched server-side,
 * staff/appointment lists filtered client-side, and static navigation commands). Every
 * source is normalised into a single {@link SearchResult} shape so the UI renders one
 * consistent, keyboard-navigable list. Icons and imperative actions are intentionally
 * left out of this layer (they belong to the UI/shell) so this stays trivially testable.
 */

/** The buckets results are grouped under. Data records first, then navigation/actions. */
export type SearchCategory = 'patients' | 'appointments' | 'visits' | 'invoices' | 'staff' | 'pages' | 'actions';

/** Canonical display order for the category groups. Records surface before navigation. */
export const CATEGORY_ORDER: SearchCategory[] = [
  'patients',
  'appointments',
  'visits',
  'invoices',
  'staff',
  'pages',
  'actions',
];

/** A single normalised search result row. */
export interface SearchResult {
  /** Stable, cross-category unique id (e.g. `patient:<id>`, `cmd:billing`). */
  id: string;
  category: SearchCategory;
  title: string;
  subtitle?: string;
  /** Small trailing label (e.g. invoice status, appointment status). */
  meta?: string;
  /** Navigation target for record and page results. Action commands navigate via `run` instead. */
  to?: string;
}

/** A group of results sharing one category, in canonical order. */
export interface SearchGroup {
  category: SearchCategory;
  items: SearchResult[];
}

/* ─── Lean source shapes (mapped from API DTOs by the infrastructure layer) ─── */

export interface PatientHit {
  id: string;
  fullName: string;
  patientNumber: string;
  phone?: string | null;
}

export interface InvoiceHit {
  id: string;
  invoiceNumber: string;
  patientName: string;
  status: string;
}

export interface StaffHit {
  userId: string;
  fullName: string;
  email?: string | null;
  phone?: string | null;
  roleNames: string[];
}

export interface AppointmentHit {
  id: string;
  patientName?: string | null;
  tempPatientName?: string | null;
  tempPatientPhone?: string | null;
  status: string;
  reason?: string | null;
  startTime: string;
}
export interface VisitHit { id: string; patientName: string; patientNumber: string; status: string; visitDate: string; match?: string | null; }

/* ─── Matching ─────────────────────────────────────────────── */

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Case-insensitive substring match. An empty/whitespace term never matches so a
 * cleared search box does not flood the list with every record.
 */
export function matchesTerm(haystack: string | null | undefined, term: string): boolean {
  const needle = normalize(term);
  if (!needle) return false;
  if (haystack == null) return false;
  return normalize(haystack).includes(needle);
}

/** True when the term matches any of the provided fields. */
function matchesAny(fields: Array<string | null | undefined>, term: string): boolean {
  return fields.some((field) => matchesTerm(field, term));
}

/** Join non-empty parts with the middot separator used across the design. */
function joinDetails(parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).join(' · ');
}

/* ─── Mapping ──────────────────────────────────────────────── */

/** Patient hits (already server-filtered) → results linking to the patient chart. */
export function mapPatientResults(hits: PatientHit[]): SearchResult[] {
  return hits.map((hit) => ({
    id: `patient:${hit.id}`,
    category: 'patients',
    title: hit.fullName,
    subtitle: joinDetails([hit.patientNumber, hit.phone]),
    to: `/patients/${hit.id}`,
  }));
}

/** Invoice hits (already server-filtered) → results linking to the invoice detail. */
export function mapInvoiceResults(hits: InvoiceHit[]): SearchResult[] {
  return hits.map((hit) => ({
    id: `invoice:${hit.id}`,
    category: 'invoices',
    title: hit.invoiceNumber,
    subtitle: hit.patientName,
    meta: hit.status,
    to: `/billing/${hit.id}`,
  }));
}

/** Staff list filtered client-side by name/email/phone/role → results linking to the staff page. */
export function filterStaffResults(hits: StaffHit[], term: string): SearchResult[] {
  return hits
    .filter((hit) => matchesAny([hit.fullName, hit.email, hit.phone, ...hit.roleNames], term))
    .map((hit) => ({
      id: `staff:${hit.userId}`,
      category: 'staff',
      title: hit.fullName,
      subtitle: joinDetails([hit.roleNames.join(', ') || null, hit.email]),
      to: '/staff',
    }));
}

/** Appointment window filtered client-side by patient name/phone → results linking to the schedule. */
export function filterAppointmentResults(hits: AppointmentHit[], term: string): SearchResult[] {
  return hits
    .filter((hit) => matchesAny([hit.patientName, hit.tempPatientName, hit.tempPatientPhone], term))
    .map((hit) => ({
      id: `appointment:${hit.id}`,
      category: 'appointments',
      title: hit.patientName ?? hit.tempPatientName ?? '',
      subtitle: joinDetails([hit.reason, hit.tempPatientPhone]),
      meta: hit.status,
      to: `/appointments/${encodeURIComponent(hit.id)}`,
    }));
}
export function mapVisitResults(hits: VisitHit[]): SearchResult[] {
  return hits.map((hit) => ({ id: `visit:${hit.id}`, category: 'visits', title: hit.patientName, subtitle: joinDetails([hit.patientNumber, hit.match]), meta: hit.status, to: `/visits/${encodeURIComponent(hit.id)}` }));
}

/* ─── Static commands (pages + quick actions) ──────────────── */

/** The minimum a static command exposes to the pure matcher; the UI adds icon + run/nav. */
export interface CommandMatchable {
  title: string;
  keywords?: string;
}

/**
 * Filter static commands by title and optional keyword synonyms. An empty term returns
 * every command so the palette can show them as suggestions before the user types.
 */
export function filterCommands<T extends CommandMatchable>(commands: T[], term: string): T[] {
  if (!normalize(term)) return [...commands];
  return commands.filter((command) => matchesAny([command.title, command.keywords], term));
}

/* ─── Grouping ─────────────────────────────────────────────── */

/** Group results by category following {@link CATEGORY_ORDER}, dropping empty groups. */
export function groupResults(results: SearchResult[], order: SearchCategory[] = CATEGORY_ORDER): SearchGroup[] {
  return order
    .map((category) => ({ category, items: results.filter((result) => result.category === category) }))
    .filter((group) => group.items.length > 0);
}

/** Flatten grouped results back to a single ordered list (used for keyboard navigation). */
export function flattenGroups(groups: SearchGroup[]): SearchResult[] {
  return groups.flatMap((group) => group.items);
}
